import { useState, useEffect, createContext, useContext, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { registerPushToken, onPushMessage, initFirebase } from "@/lib/pushNotifications";
import "@fontsource/fraunces/300.css";
import "@fontsource/fraunces/400.css";
import "@fontsource/fraunces/500.css";
import "@fontsource/nunito/400.css";
import "@fontsource/nunito/500.css";
import "@fontsource/nunito/600.css";
import "@fontsource/nunito/700.css";
import "@fontsource/dm-serif-display/400.css";
import "@fontsource/dm-sans/400.css";
import "@fontsource/dm-sans/500.css";
import "@fontsource/dm-sans/600.css";
import "@fontsource/outfit/400.css";
import "@fontsource/outfit/500.css";
import "@fontsource/outfit/600.css";
import "@fontsource/lora/400.css";
import "@fontsource/lora/500.css";
import "@fontsource/lora/600.css";
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/raleway/400.css";
import "@fontsource/raleway/400-italic.css";
import "@fontsource/raleway/500.css";
import "@fontsource/raleway/500-italic.css";
import "@fontsource/raleway/600.css";
import "@fontsource/raleway/600-italic.css";
import "@fontsource/raleway/700.css";
import "@fontsource/raleway/700-italic.css";
import { Toaster } from "@/components/ui/sonner";
import { I18nProvider, useI18n } from "@/i18n/I18nProvider";
import { VoiceProvider } from "@/contexts/VoiceContext";
import { TooltipProvider } from "@/components/ui/tooltip";

import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import OnboardingPage from "@/pages/OnboardingPage";
import ChatPage from "@/pages/ChatPage";
import MoodPage from "@/pages/MoodPage";
import GoalsPage from "@/pages/GoalsPage";
import PricingPage from "@/pages/PricingPage";
import SettingsPage from "@/pages/SettingsPage";
import InsightSharePage from "@/pages/InsightSharePage";
import FamilyPage from "@/pages/FamilyPage";
import ConversationSharePage from "@/pages/ConversationSharePage";
import BookmarksPage from "@/pages/BookmarksPage";
import TherapyPage from "@/pages/TherapyPage";
import TimelinePage from "@/pages/TimelinePage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ParentDashboardPage from "@/pages/ParentDashboardPage";
import GuidePage from "@/pages/GuidePage";
import AdminDashboard from "@/pages/AdminDashboard";
import SharePortalPage from "@/pages/SharePortalPage";
import CalendarPage from "@/pages/CalendarPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import PaymentCancelPage from "@/pages/PaymentCancelPage";
import SharedExportPage from "@/pages/SharedExportPage";
import GiftPage from "@/pages/GiftPage";
import GiftSuccessPage from "@/pages/GiftSuccessPage";
import GiftRedeemPage from "@/pages/GiftRedeemPage";
import BulkGiftPage from "@/pages/BulkGiftPage";
import StatusPage from "@/pages/StatusPage";
import PressKitPage from "@/pages/PressKitPage";
import InsightsPage from "@/pages/InsightsPage";
import GroupAdminPage from "@/pages/GroupAdminPage";

/* ─── Marketing Website Pages ─── */
import WebsiteLayout from "@/website/WebsiteLayout";
import WebsiteHomePage from "@/website/HomePage";
import WebsiteAboutPage from "@/website/AboutPage";
import WebsiteFeaturesPage from "@/website/FeaturesPage";
import WebsitePricingPage from "@/website/PricingPage";
import WebsiteContactPage from "@/website/ContactPage";
import WebsitePrivacyPage from "@/website/PrivacyPage";
import WebsiteTermsPage from "@/website/TermsPage";

export const API = process.env.REACT_APP_BACKEND_URL + "/api";

// ===== AUTH CONTEXT =====
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ===== THEME CONTEXT =====
const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

export const THEMES = [
  { key: "sage", name: "Crimson & Gold", vibe: "Bold, commanding", preview: "bg-[#c62828]",
    colors: { bg: "#f7f4ee", fg: "#2b2020", primary: "#c62828", accent: "#e8a817", card: "#fbf9f5", muted: "#e8e2d5",
              darkBg: "#1a1414", darkFg: "#f7f4ee", darkPrimary: "#ef4444", darkCard: "#241c1c" }},
  { key: "ocean", name: "Ocean Deep", vibe: "Bold, serene", preview: "bg-[#1a6bc4]",
    colors: { bg: "#f0f4fa", fg: "#152540", primary: "#1a6bc4", accent: "#0db8b8", card: "#f7fafd", muted: "#e0e8f0",
              darkBg: "#0d1526", darkFg: "#f0f5fa", darkPrimary: "#5ea5f5", darkCard: "#15203a" }},
  { key: "sunset", name: "Sunset Blaze", vibe: "Warm, vibrant", preview: "bg-[#e85530]",
    colors: { bg: "#fdf5f0", fg: "#30180e", primary: "#e85530", accent: "#e83088", card: "#fef9f5", muted: "#ede0d8",
              darkBg: "#1e1410", darkFg: "#fdf0e8", darkPrimary: "#f07850", darkCard: "#2a1e18" }},
  { key: "lavender", name: "Royal Violet", vibe: "Rich, elegant", preview: "bg-[#7c3aed]",
    colors: { bg: "#f5f0fa", fg: "#1e1238", primary: "#7c3aed", accent: "#d63094", card: "#faf7fd", muted: "#e8e0f0",
              darkBg: "#16101e", darkFg: "#f0ecfa", darkPrimary: "#a875ff", darkCard: "#201830" }},
  { key: "tropical", name: "Tropical Bold", vibe: "Energetic, joyful", preview: "bg-[#0d9488]",
    colors: { bg: "#f8f6e8", fg: "#122e28", primary: "#0d9488", accent: "#e83088", card: "#fcfaf0", muted: "#e6e2cc",
              darkBg: "#101e1c", darkFg: "#f5f0d8", darkPrimary: "#20b8aa", darkCard: "#182e2a" }},
];

export const FONTS = [
  { key: "default", name: "Century Gothic", vibe: "Elegant Geometric", sample: "font-['Raleway']" },
  { key: "classic", name: "DM Serif + DM Sans", vibe: "Classic", sample: "font-['DM_Serif_Display']" },
  { key: "modern", name: "Outfit", vibe: "Modern", sample: "font-['Outfit']" },
  { key: "literary", name: "Lora + Space Grotesk", vibe: "Literary", sample: "font-['Lora']" },
  { key: "fraunces", name: "Fraunces + Nunito", vibe: "Traditional", sample: "font-['Fraunces']" },
];

export const FONT_SIZES = [
  { key: "small", name: "Small", base: "14px", desc: "Compact" },
  { key: "medium", name: "Medium", base: "16px", desc: "Default" },
  { key: "large", name: "Large", base: "18px", desc: "Comfortable" },
  { key: "xlarge", name: "Extra Large", base: "20px", desc: "Accessibility" },
];

function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => localStorage.getItem("omni_theme") || "sage");
  const [font, setFontState] = useState(() => localStorage.getItem("omni_font") || "default");
  const [fontSize, setFontSizeState] = useState(() => localStorage.getItem("omni_fontsize") || "medium");
  const [darkMode, setDarkModeState] = useState(() => localStorage.getItem("omni_darkmode") === "true");
  const [highContrast, setHighContrastState] = useState(() => localStorage.getItem("omni_highcontrast") === "true");
  const [reduceMotion, setReduceMotionState] = useState(() => localStorage.getItem("omni_reducemotion") === "true");
  const [customTheme, setCustomThemeState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("omni_custom_theme")) || null; } catch { return null; }
  });

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("omni_theme", theme);
    // Apply custom theme CSS variables when 'custom' is active
    if (theme === "custom" && customTheme) {
      const root = document.documentElement;
      root.style.setProperty("--primary", customTheme.primaryHSL);
      root.style.setProperty("--primary-foreground", "0 0% 100%");
      root.style.setProperty("--accent", customTheme.accentHSL);
      root.style.setProperty("--accent-foreground", "0 0% 100%");
      root.style.setProperty("--background", customTheme.bgHSL);
      root.style.setProperty("--ring", customTheme.primaryHSL);
      root.style.setProperty("--card", customTheme.cardHSL);
      root.style.setProperty("--card-foreground", customTheme.fgHSL);
      root.style.setProperty("--foreground", customTheme.fgHSL);
      root.style.setProperty("--popover", customTheme.cardHSL);
      root.style.setProperty("--popover-foreground", customTheme.fgHSL);
      root.style.setProperty("--muted", customTheme.mutedHSL);
      root.style.setProperty("--muted-foreground", customTheme.mutedFgHSL);
      root.style.setProperty("--border", customTheme.borderHSL);
      root.style.setProperty("--input", customTheme.borderHSL);
      root.style.setProperty("--secondary", customTheme.mutedHSL);
      root.style.setProperty("--secondary-foreground", customTheme.fgHSL);
    } else {
      // Clear inline styles when switching away from custom
      const props = ["--primary","--primary-foreground","--accent","--accent-foreground","--background","--ring","--card","--card-foreground","--foreground","--popover","--popover-foreground","--muted","--muted-foreground","--border","--input","--secondary","--secondary-foreground"];
      props.forEach(p => document.documentElement.style.removeProperty(p));
    }
  }, [theme, customTheme]);

  useEffect(() => {
    if (font === "default") {
      document.documentElement.removeAttribute("data-font");
    } else {
      document.documentElement.setAttribute("data-font", font);
    }
    localStorage.setItem("omni_font", font);
  }, [font]);

  useEffect(() => {
    document.documentElement.setAttribute("data-fontsize", fontSize);
    localStorage.setItem("omni_fontsize", fontSize);
  }, [fontSize]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.setAttribute("data-mode", "dark");
    } else {
      document.documentElement.removeAttribute("data-mode");
    }
    localStorage.setItem("omni_darkmode", String(darkMode));
  }, [darkMode]);

  useEffect(() => {
    if (highContrast) {
      document.documentElement.setAttribute("data-contrast", "high");
    } else {
      document.documentElement.removeAttribute("data-contrast");
    }
    localStorage.setItem("omni_highcontrast", String(highContrast));
  }, [highContrast]);

  useEffect(() => {
    if (reduceMotion) {
      document.documentElement.setAttribute("data-motion", "reduce");
    } else {
      document.documentElement.removeAttribute("data-motion");
    }
    localStorage.setItem("omni_reducemotion", String(reduceMotion));
  }, [reduceMotion]);

  const setTheme = (t) => setThemeState(t);
  const setFont = (f) => setFontState(f);
  const setFontSize = (s) => setFontSizeState(s);
  const setDarkMode = (d) => setDarkModeState(d);
  const toggleDarkMode = () => setDarkModeState(prev => !prev);
  const toggleHighContrast = () => setHighContrastState(prev => !prev);
  const toggleReduceMotion = () => setReduceMotionState(prev => !prev);
  const setCustomTheme = (ct) => {
    setCustomThemeState(ct);
    localStorage.setItem("omni_custom_theme", JSON.stringify(ct));
  };

  return (
    <ThemeContext.Provider value={{ theme, font, fontSize, darkMode, highContrast, reduceMotion, customTheme, setTheme, setFont, setFontSize, setDarkMode, toggleDarkMode, toggleHighContrast, toggleReduceMotion, setCustomTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem("omni_token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const themeCtx = useTheme();
  const { setLang } = useI18n();

  // Global 401 interceptor — auto-logout on expired/invalid tokens
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err.response?.status === 401 && token) {
          localStorage.removeItem("omni_token");
          setToken(null);
          setUser(null);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, [token]);

  useEffect(() => {
    if (token) {
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => {
          setUser(res.data);
          const prefs = res.data.preferences;
          if (prefs?.theme) themeCtx.setTheme(prefs.theme);
          if (prefs?.font) themeCtx.setFont(prefs.font);
          if (prefs?.language) setLang(prefs.language);
          if (prefs?.font_size) themeCtx.setFontSize(prefs.font_size);
        })
        .catch(() => { setToken(null); setUser(null); localStorage.removeItem("omni_token"); })
        .finally(() => setLoading(false));
    } else {
      setUser(null);
      setLoading(false);
    }
  }, [token]);

  // Register push notifications after user is authenticated
  useEffect(() => {
    if (token && user) {
      initFirebase().then((ready) => {
        if (ready) {
          registerPushToken(token);
          onPushMessage((payload) => {
            if (Notification.permission === "granted" && payload.notification) {
              new Notification(payload.notification.title, {
                body: payload.notification.body,
                icon: "/icons/icon-192x192.png",
              });
            }
          });
        }
      });
    }
  }, [token, user]);

  const login = (newToken, userData) => {
    localStorage.setItem("omni_token", newToken);
    if (userData?.username) localStorage.setItem("omni_known_user", userData.username);
    setToken(newToken);
    setUser(userData);
    const prefs = userData.preferences;
    if (prefs?.theme) themeCtx.setTheme(prefs.theme);
    if (prefs?.font) themeCtx.setFont(prefs.font);
    if (prefs?.language) setLang(prefs.language);
    if (prefs?.font_size) themeCtx.setFontSize(prefs.font_size);
  };

  const logout = () => {
    localStorage.removeItem("omni_token");
    setToken(null);
    setUser(null);
  };

  const updateUser = (data) => setUser(prev => ({ ...prev, ...data }));

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ token, user, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { token, user } = useAuth();
  if (!token || !user) return <Navigate to="/auth" replace />;
  if (!user.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return children;
}

function OnboardingRoute({ children }) {
  const { token, user } = useAuth();
  if (!token || !user) return <Navigate to="/auth" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
      <TooltipProvider delayDuration={300}>
      <ThemeProvider>
        <AuthProvider>
          <VoiceProvider>
          <Routes>
            {/* ═══ Marketing Website (omnimodules.com) ═══ */}
            <Route path="/" element={<WebsiteLayout><WebsiteHomePage /></WebsiteLayout>} />
            <Route path="/about" element={<WebsiteLayout><WebsiteAboutPage /></WebsiteLayout>} />
            <Route path="/features" element={<WebsiteLayout><WebsiteFeaturesPage /></WebsiteLayout>} />
            <Route path="/plans" element={<WebsiteLayout><WebsitePricingPage /></WebsiteLayout>} />
            <Route path="/contact" element={<WebsiteLayout><WebsiteContactPage /></WebsiteLayout>} />
            <Route path="/privacy" element={<WebsiteLayout><WebsitePrivacyPage /></WebsiteLayout>} />
            <Route path="/terms" element={<WebsiteLayout><WebsiteTermsPage /></WebsiteLayout>} />

            {/* ═══ Public Pages (self-contained layout) ═══ */}
            <Route path="/status" element={<StatusPage />} />
            <Route path="/press" element={<PressKitPage />} />

            {/* ═══ App Landing (legacy) ═══ */}
            <Route path="/welcome" element={<LandingPage />} />

            {/* ═══ App Routes ═══ */}
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/onboarding" element={<OnboardingRoute><OnboardingPage /></OnboardingRoute>} />
            <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/chat/:conversationId" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/mood" element={<ProtectedRoute><MoodPage /></ProtectedRoute>} />
            <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
            <Route path="/pricing" element={<ProtectedRoute><PricingPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/family" element={<ProtectedRoute><FamilyPage /></ProtectedRoute>} />
            <Route path="/bookmarks" element={<ProtectedRoute><BookmarksPage /></ProtectedRoute>} />
            <Route path="/therapy" element={<ProtectedRoute><TherapyPage /></ProtectedRoute>} />
            <Route path="/timeline" element={<ProtectedRoute><TimelinePage /></ProtectedRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/parent" element={<ParentDashboardPage />} />
            <Route path="/guide" element={<ProtectedRoute><GuidePage /></ProtectedRoute>} />
            <Route path="/share-portal" element={<ProtectedRoute><SharePortalPage /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><CalendarPage /></ProtectedRoute>} />
            <Route path="/insights" element={<ProtectedRoute><InsightsPage /></ProtectedRoute>} />
            <Route path="/group-admin" element={<ProtectedRoute><GroupAdminPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
            <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
            <Route path="/shared-export/:shareId" element={<SharedExportPage />} />
            <Route path="/gift" element={<ProtectedRoute><GiftPage /></ProtectedRoute>} />
            <Route path="/gift/bulk" element={<ProtectedRoute><BulkGiftPage /></ProtectedRoute>} />
            <Route path="/gift/bulk/success" element={<ProtectedRoute><GiftSuccessPage /></ProtectedRoute>} />
            <Route path="/gift/success" element={<ProtectedRoute><GiftSuccessPage /></ProtectedRoute>} />
            <Route path="/gift/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
            <Route path="/gift/redeem" element={<GiftRedeemPage />} />
            <Route path="/gift/redeem/:giftCode" element={<GiftRedeemPage />} />
            <Route path="/share/:insightId" element={<InsightSharePage />} />
            <Route path="/share/conversation/:shareId" element={<ConversationSharePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </VoiceProvider>
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </ThemeProvider>
      </TooltipProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}

export default App;
